/**
 * The event page's body: the breadcrumb, the event's name, short line and
 * facts (its one date and time in the visitor's zone, format, price, spots
 * left), its description, where it happens and the cancellation line, laid out
 * beside the booking column (the service page's layout). Each line is drawn
 * only when the studio set it.
 */
import type { SiteEventDto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'event-page',
  description:
    "An event's page: breadcrumb, name, short line, date and time, format, price and spots left, description, location and cancellation, beside its photos and booking column.",
  options: {
    aboutHeading: {
      type: 'string',
      default: 'About this event',
      maxLength: 80,
      description: 'The heading over the description; null for none.',
    },
  },
  parts: {
    page: { description: 'The whole page body.', required: false },
    crumbs: { description: 'The breadcrumb: the studio, Events, then the event.', required: false },
    'crumb-home': { description: "The studio's name in the breadcrumb; opens the home page.", required: false },
    'crumb-events': { description: 'Events in the breadcrumb; opens the events page.', required: false },
    'crumb-book': { description: "On a custom site, the site's booking page in the breadcrumb, by its own name; opens it.", required: false },
    'crumb-here': { description: "The event's name in the breadcrumb.", required: false },
    photos: { description: 'Where the photo gallery sits.', required: false },
    name: { description: "The event's name.", required: true, role: 'title' },
    summary: { description: "The event's short line.", required: false },
    facts: { description: 'The row of facts: when, format, price, spots.', required: false },
    when: { description: "The event's date and start time in the visitor's zone.", required: false },
    format: { description: 'In person or online.', required: false },
    price: { description: 'The price or Free.', required: true },
    spots: { description: 'How many places are left, or Full.', required: false },
    'about-heading': { description: 'The heading over the description.', required: false },
    description: { description: "The event's full description.", required: false },
    details: { description: 'The location and cancellation lines.', required: false },
    location: { description: 'Where the event happens.', required: false },
    cancellation: { description: "The studio's cancellation line.", required: false },
    booking: { description: 'Where the booking column sits.', required: false },
    error: { description: 'The line shown when the page could not load.', required: false },
  },
  actions: {},
  events: {},
});

/** What the event page knows. */
export interface EventPageState {
  /** loading until the event's read lands; not-found when the studio does not list it (the read answers 4xx); error when the read failed otherwise. */
  readonly status: 'loading' | 'ready' | 'not-found' | 'error';
  /** The event, or null until it lands. */
  readonly event: SiteEventDto | null;
  /** The studio's name for the breadcrumb, or null until it is known. */
  readonly studioName: string | null;
  /**
   * On a studio's custom site, its own home and booking page, for the breadcrumb (web-studio-sites D13): their
   * labels and addresses as the site names them. Null on the standard website.
   */
  readonly siteLinks: {
    readonly home: { readonly path: string; readonly label: string };
    readonly book: { readonly path: string; readonly label: string };
  } | null;
  /** The visitor's IANA time zone; the date and time are shown in it. */
  readonly timezone: string;
}

/** The event page takes no action of its own; its photos and booking column are their own pieces. */
export type EventPageActions = Record<string, never>;

/** The event page reports no event of its own. */
export type EventPageEvents = Record<string, never>;
